/**
 * Execution Live Stream — operator signal preferences.
 *
 * Read-only personalization layer. Filters which event categories and
 * kind-groups appear in the Execution rail. Persists per role+user in
 * localStorage. No backend, no telemetry, no writers.
 *
 * Defaults (per spec):
 *   ops_manager          → qa, field, scheduling, issues       (all kindGroups on)
 *   head_of_installation → qa, field, phaseEvents               (others off)
 *   admin / super_admin  → all on
 *   anything else        → all on (safe fallback)
 *
 * Visibility rule (intersection):
 *   show event ⇔ categories[event.category] && (kindGroup ? kindGroups[kindGroup] : true)
 */

import type { UserRole } from "@/data/RoleContext";
import type {
  ExecutionEvent,
  ExecutionEventCategory,
} from "@/data/execution/eventStream";

export const SIGNAL_CATEGORIES = [
  "qa",
  "field",
  "scheduling",
  "issues",
] as const satisfies readonly ExecutionEventCategory[];

export type SignalCategory = (typeof SIGNAL_CATEGORIES)[number];

export const SIGNAL_KIND_GROUPS = [
  "holdbacks",
  "callbacks",
  "substitutions",
  "phaseEvents",
] as const;

export type SignalKindGroup = (typeof SIGNAL_KIND_GROUPS)[number];

export interface SignalPreferences {
  categories: Record<SignalCategory, boolean>;
  kindGroups: Record<SignalKindGroup, boolean>;
}

const ALL_ON: SignalPreferences = {
  categories: { qa: true, field: true, scheduling: true, issues: true },
  kindGroups: {
    holdbacks: true,
    callbacks: true,
    substitutions: true,
    phaseEvents: true,
  },
};

function withCategories(enabled: SignalCategory[]): SignalPreferences {
  const set = new Set(enabled);
  return {
    categories: {
      qa: set.has("qa"),
      field: set.has("field"),
      scheduling: set.has("scheduling"),
      issues: set.has("issues"),
    },
    kindGroups: { ...ALL_ON.kindGroups },
  };
}

export function getDefaultSignalPreferences(role: UserRole): SignalPreferences {
  switch (role) {
    case "ops_manager":
      return withCategories(["qa", "field", "scheduling", "issues"]);
    case "head_of_installation":
      return {
        categories: { qa: true, field: true, scheduling: false, issues: false },
        kindGroups: {
          holdbacks: false,
          callbacks: false,
          substitutions: false,
          phaseEvents: true,
        },
      };
    case "admin":
    case "super_admin":
      return { ...ALL_ON, categories: { ...ALL_ON.categories }, kindGroups: { ...ALL_ON.kindGroups } };
    default:
      return { ...ALL_ON, categories: { ...ALL_ON.categories }, kindGroups: { ...ALL_ON.kindGroups } };
  }
}

/* ── kind → kindGroup map ────────────────────────────────────────────── */

export function kindGroupOf(kind: ExecutionEvent["kind"]): SignalKindGroup | null {
  switch (kind) {
    case "callback_created":
      return "callbacks";
    case "holdback_action":
      return "holdbacks";
    case "substitution_request":
      return "substitutions";
    case "phase_submitted":
    case "phase_qa_passed":
    case "phase_qa_failed":
      return "phaseEvents";
    case "field_material_issue":
      return null; // category-only (field)
    default:
      return null;
  }
}

/* ── filter application ──────────────────────────────────────────────── */

export function applySignalPreferences(
  events: ExecutionEvent[],
  prefs: SignalPreferences,
): ExecutionEvent[] {
  return events.filter((e) => {
    if (!prefs.categories[e.category as SignalCategory]) return false;
    const grp = kindGroupOf(e.kind);
    if (grp && !prefs.kindGroups[grp]) return false;
    return true;
  });
}

/* ── localStorage persistence ─────────────────────────────────────────── */

const STORAGE_PREFIX = "exec.rail.signalPrefs.v1";

export function storageKeyFor(role: UserRole): string {
  return `${STORAGE_PREFIX}:${role}`;
}

export function loadSignalPreferences(role: UserRole): SignalPreferences {
  if (typeof window === "undefined") return getDefaultSignalPreferences(role);
  try {
    const raw = window.localStorage.getItem(storageKeyFor(role));
    if (!raw) return getDefaultSignalPreferences(role);
    const parsed = JSON.parse(raw) as Partial<SignalPreferences> | null;
    if (!parsed || typeof parsed !== "object") return getDefaultSignalPreferences(role);
    const base = getDefaultSignalPreferences(role);
    return {
      categories: { ...base.categories, ...(parsed.categories ?? {}) },
      kindGroups: { ...base.kindGroups, ...(parsed.kindGroups ?? {}) },
    };
  } catch {
    return getDefaultSignalPreferences(role);
  }
}

export function saveSignalPreferences(
  role: UserRole,
  prefs: SignalPreferences,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKeyFor(role), JSON.stringify(prefs));
  } catch {
    /* swallow quota / private-mode errors */
  }
}

export function resetSignalPreferences(role: UserRole): SignalPreferences {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(storageKeyFor(role));
    } catch {
      /* ignore */
    }
  }
  return getDefaultSignalPreferences(role);
}

export const SIGNAL_CATEGORY_LABEL: Record<SignalCategory, string> = {
  qa: "QA",
  field: "Field",
  scheduling: "Scheduling",
  issues: "Issues",
};

export const SIGNAL_KIND_GROUP_LABEL: Record<SignalKindGroup, string> = {
  holdbacks: "Holdbacks",
  callbacks: "Callbacks",
  substitutions: "Substitutions",
  phaseEvents: "Phase Events",
};
